import type { ReactiveSet } from '@solid-primitives/set'
import { Title } from '@solidjs/meta'
import { useSearchParams } from '@solidjs/router'
import { type Component, createEffect, createMemo, createSignal, Show } from 'solid-js'
import { createStore } from 'solid-js/store'
import { z } from 'zod'
import Badge from '~/components/Badge'
import Button from '~/components/Button'
import ButtonGroup from '~/components/ButtonGroup'
import Card from '~/components/Card'
import Checkbox from '~/components/Checkbox'
import Chip from '~/components/Chip'
import FabMenu from '~/components/FabMenu'
import Form from '~/components/Form'
import InfiniteList from '~/components/InfiniteList'
import Input from '~/components/Input'
import Menu from '~/components/Menu'
import Modal from '~/components/Modal'
import SwipeAction from '~/components/SwipeAction'
import Table from '~/components/Table'
import Textarea from '~/components/Textarea'
import useAlert from '~/components/useAlert'
import { useToast } from '~/components/useToast'
import { useCompanySettings } from '~/hooks/useCompanySettings'
import { useDebouncedSignal } from '~/hooks/useDebouncedSignal'
import { useForm } from '~/hooks/useForm'
import { useIsMobile } from '~/hooks/useIsMobile'
import { usePermissions } from '~/hooks/usePermissions'
import { useServerTable } from '~/hooks/useServerTable'
import { type Path, useNavigate } from '~/router'
import { type QuoteType, quoteService } from '~/services/quote.service'
import { resolveError } from '~/utils/error'
import { P } from '~/utils/permissions'
import { formatMoney } from '~/utils/settings'

const statusColors: Record<string, 'default' | 'success' | 'warning' | 'danger' | 'info'> = {
  draft: 'default',
  sent: 'info',
  approved: 'success',
  rejected: 'danger',
  expired: 'warning',
  cancelled: 'danger'
}

const statusLabels: Record<string, string> = {
  draft: 'Borrador',
  sent: 'Enviada',
  approved: 'Aprobada',
  rejected: 'Rechazada',
  expired: 'Expirada',
  cancelled: 'Cancelada'
}

const QuotesPage: Component = () => {
  const toast = useToast()
  const alert = useAlert()
  const navigate = useNavigate()
  const { can } = usePermissions()
  const { settings } = useCompanySettings()
  const isMobile = useIsMobile()
  const [searchParams, setSearchParams] = useSearchParams()

  // Búsqueda y filtros server-side
  const sanitizeStatus = (value: string | undefined): string => (value && value in statusLabels ? value : '')
  const filterStatus = createMemo(() => sanitizeStatus(searchParams.status as string | undefined))
  const [_search, debouncedSearch, setSearch] = useDebouncedSignal('')

  // El filtro de estado vive en la URL (?status=...) para que sobreviva a la recarga
  // y se pueda compartir. Fuente única: la URL (mismo rebote que se corrigió en pedidos).
  const handleFilterChange = (value: string) => {
    const next: Record<string, string | string[] | undefined> = { ...searchParams }
    next.status = value || undefined
    setSearchParams(next, { replace: true })
  }

  const buildParams = (): Record<string, string | number | undefined> => {
    const q = debouncedSearch().trim()
    const p: Record<string, string | number | undefined> = {}
    if (q) p.title = `%${q}%`
    if (filterStatus()) p.status = filterStatus()
    return p
  }

  const table = useServerTable<QuoteType>({
    fetcher: params => quoteService.list(params as Parameters<typeof quoteService.list>[0]),
    params: buildParams
  })

  // ── Scroll infinito móvil ────────────────────────────────────────────────
  const [mobileItems, setMobileItems] = createStore<QuoteType[]>([])
  const [mobileLoading, setMobileLoading] = createSignal(false)
  const [mobileTotal, setMobileTotal] = createSignal(0)
  const hasMore = () => mobileItems.length < mobileTotal()

  const mobileFetch = async (reset: boolean) => {
    if (!reset && mobileLoading()) return
    setMobileLoading(true)
    try {
      const page = reset ? 1 : Math.floor(mobileItems.length / table.perPage()) + 1
      const res = await quoteService.list({ ...buildParams(), page, limit: table.perPage() })
      setMobileTotal(res.meta.total)
      setMobileItems(reset ? res.items : prev => [...prev, ...res.items])
    } catch {
      // error silencioso: el sentinela reintentará en el siguiente scroll
    } finally {
      setMobileLoading(false)
    }
  }

  const [filtersKey, setFiltersKey] = createSignal('')
  createEffect(() => {
    if (!isMobile()) return
    const key = JSON.stringify([filterStatus(), debouncedSearch()])
    if (key === filtersKey()) return
    setFiltersKey(key)
    void mobileFetch(true)
  })

  const loadMore = () => {
    if (!hasMore() || mobileLoading()) return
    void mobileFetch(false)
  }

  const [sending, setSending] = createSignal<QuoteType | null>(null)
  const [pdfBusyId, setPdfBusyId] = createSignal<number | null>(null)

  const sendSchema = z.object({
    to: z.string().trim().min(1, 'El correo es obligatorio').email('Correo inválido'),
    subject: z.string().trim().min(1, 'El asunto es obligatorio'),
    message: z.string().optional().default('')
  })

  type SendFormValuesType = z.infer<typeof sendSchema>

  const sendForm = useForm<SendFormValuesType>({
    schema: sendSchema,
    initialValues: () => ({
      to: ((sending() as unknown as { customerEmail?: string })?.customerEmail ?? sending()?.meta?.lastSentTo ?? '') as string,
      subject: sending() ? `Propuesta comercial ${sending()!.number} — ${sending()!.title}` : '',
      message: ''
    }),
    onSubmit: async values => {
      const quote = sending()
      if (!quote) return
      await quoteService.send(quote.id, {
        to: String(values.to ?? '').trim(),
        subject: String(values.subject ?? '').trim() || undefined,
        message: String(values.message ?? '').trim() || undefined
      })
      toast.addToast({ title: 'Propuesta enviada', type: 'success' })
      setSending(null)
      await table.refetch()
    },
    onError: err => toast.addToast({ title: resolveError(err), type: 'danger' })
  })

  const handleDelete = async (quote: QuoteType) => {
    const ok = await alert.confirmDelete({
      title: 'Eliminar cotización',
      message: `Se eliminará la propuesta ${quote.number} y sus líneas.`
    })
    if (!ok) return

    try {
      await quoteService.delete(quote.id)
      toast.addToast({ title: 'Cotización eliminada', type: 'success' })
      setMobileItems(prev => prev.filter(i => i.id !== quote.id))
      await table.refetch()
    } catch (err: unknown) {
      toast.addToast({ title: resolveError(err), type: 'danger' })
    }
  }

  // ── Selección masiva ────────────────────────────────────────────────────
  const [selectedIds, setSelectedIds] = createSignal<ReadonlySet<number>>(new Set())

  const handleSelectionChange = (selected: ReactiveSet<unknown>) => {
    const next = new Set<number>()
    for (const value of selected.values()) next.add(value as number)
    setSelectedIds(next)
  }

  const toggleSelect = (id: number) => {
    setSelectedIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const handleBulkDelete = async () => {
    const ids = [...selectedIds()]
    if (ids.length === 0) return
    const ok = await alert.confirmDelete({
      title: 'Eliminar cotizaciones',
      message: `¿Eliminar ${ids.length} ${ids.length === 1 ? 'cotización' : 'cotizaciones'} y sus líneas? Esta acción no se puede deshacer.`
    })
    if (!ok) return
    try {
      const res = await quoteService.bulkDelete(ids)
      toast.addToast({ title: `${res.deleted} ${res.deleted === 1 ? 'cotización eliminada' : 'cotizaciones eliminadas'}`, type: 'success' })
      setSelectedIds(new Set<number>())
      setMobileItems(prev => prev.filter(i => !ids.includes(i.id)))
      await table.refetch()
    } catch (err: unknown) {
      toast.addToast({ title: resolveError(err), type: 'danger' })
    }
  }

  const handlePdf = async (quote: QuoteType) => {
    setPdfBusyId(quote.id)
    try {
      await quoteService.openPdf(quote.id)
    } catch (err: unknown) {
      toast.addToast({ title: resolveError(err), type: 'danger' })
    } finally {
      setPdfBusyId(null)
    }
  }

  /** Descarga el archivo en vez de abrirlo en otra pestaña. */
  const handleDownload = async (quote: QuoteType) => {
    setPdfBusyId(quote.id)
    try {
      await quoteService.downloadPdf(quote.id, quote.number)
    } catch (err: unknown) {
      toast.addToast({ title: resolveError(err), type: 'danger' })
    } finally {
      setPdfBusyId(null)
    }
  }

  /** Pestañas por estado: usa los labels estáticos. */
  const filterOptions = createMemo(() => [
    { label: 'Todas', value: '' },
    ...Object.entries(statusLabels).map(([value, label]) => ({
      label,
      value,
      color: statusColors[value]
    }))
  ])

  const columns = [
    { header: 'Número', accessor: 'number' as const, sticky: 'left' as const, minWidth: 160 },
    {
      header: 'Título',
      accessor: (q: QuoteType) => (
        <div>
          <div class="flex items-center gap-2">
            <span class="font-medium text-foreground">{q.title}</span>
            {/* La pidió un cliente desde la ficha pública: nadie del equipo la ha mirado. */}
            <Show when={q.meta?.origin === 'catalogo-publico'}>
              <Badge color="success" variant="soft" size="sm">
                Del catálogo
              </Badge>
            </Show>
          </div>
          <div class="text-xs text-foreground-subtle">{q.items?.length ?? 0} servicio(s)</div>
        </div>
      )
    },
    { header: 'Cliente', accessor: (q: QuoteType) => q.customer?.name ?? '-' },
    {
      header: 'Total',
      accessor: (q: QuoteType) => formatMoney(q.total, q.currency, settings()?.currencies ?? [])
    },
    {
      header: 'Estado',
      accessor: (q: QuoteType) => (
        <Chip color={statusColors[q.status]} variant="soft" size="xs">
          {statusLabels[q.status]}
        </Chip>
      )
    }
  ]

  return (
    <>
      <Title>Cotizaciones | Florez Jaramillo</Title>
      <Show when={table.error()}>
        <div class="rounded-xl border border-danger-soft bg-danger-soft/30 px-4 py-3 text-sm text-danger mb-4">{resolveError(table.error())}</div>
      </Show>

      <Show when={!isMobile()}>
        <Table<QuoteType>
          title="Cotizaciones"
          subtitle="Propuestas comerciales, su PDF y su envío"
          headerActions={
            <div class="hidden sm:flex items-center gap-2">
              <Show when={can(P.quotes.create)}>
                <Button onClick={() => navigate('/admin/cotizaciones/nueva')} prependIcon="material-symbols-light:add-circle-outline">
                  Nueva Cotización
                </Button>
              </Show>
            </div>
          }
          data={table.data()}
          columns={columns}
          keyField="id"
          loading={table.loading()}
          searchable
          searchPlaceholder="Buscar por número o título..."
          onSearch={setSearch}
          serverSide
          selectable={can(P.quotes.delete)}
          selectableColor="danger"
          onSelectionChange={handleSelectionChange}
          onDeleteSelected={() => void handleBulkDelete()}
          total={table.total()}
          page={table.page()}
          perPage={table.perPage()}
          onPageChange={table.onPageChange}
          onPerPageChange={table.onPerPageChange}
          showFilters
          headSticky
          filterField="status"
          activeFilter={filterStatus()}
          onFilterChange={handleFilterChange}
          filterOptions={filterOptions()}
          emptyMessage="Aún no has creado cotizaciones"
          actions={quote => (
            <ButtonGroup size="sm" variant="soft">
              <Menu
                trigger={<Button color="info" icon="material-symbols-light:picture-as-pdf-outline" disabled={pdfBusyId() === quote.id} tooltip="Acciones PDF" aria-label="Acciones PDF" />}
                items={[
                  { label: 'Ver PDF', icon: 'material-symbols-light:visibility-outline', onClick: () => handlePdf(quote) },
                  { label: 'Descargar PDF', icon: 'material-symbols-light:download', onClick: () => handleDownload(quote) },
                  ...(can(P.quotes.update) ? [{ label: 'Enviar por correo', icon: 'material-symbols-light:mail-outline', onClick: () => setSending(quote) }] : [])
                ]}
              />
              <Show when={can(P.quotes.update)}>
                <Button color="warning" onClick={() => navigate(`/admin/cotizaciones/${quote.id}` as Path)} tooltip="Editar" icon="material-symbols-light:edit-outline" />
              </Show>
              <Show when={can(P.quotes.delete)}>
                <Button color="danger" onClick={() => handleDelete(quote)} tooltip="Eliminar" icon="material-symbols-light:delete-outline" />
              </Show>
            </ButtonGroup>
          )}
        />
      </Show>

      {/* Vista móvil: en pantallas pequeñas la tabla desborda, así que se
          sustituye por tarjetas con acciones deslizables y scroll infinito. */}
      <Show when={isMobile()}>
        <Show when={can(P.quotes.delete) && selectedIds().size > 0}>
          <div class="mb-3 flex items-center justify-between gap-2 rounded-xl border border-danger-soft bg-danger-soft/30 px-4 py-3">
            <span class="text-sm font-semibold text-danger">
              {selectedIds().size} {selectedIds().size === 1 ? 'seleccionado' : 'seleccionados'}
            </span>
            <Button size="sm" color="danger" variant="soft" icon="material-symbols-light:delete-outline" onClick={() => void handleBulkDelete()}>
              Eliminar
            </Button>
          </div>
        </Show>
        <InfiniteList<QuoteType>
          items={mobileItems}
          keyField={q => q.id}
          hasMore={hasMore()}
          loading={mobileLoading()}
          onLoadMore={loadMore}
          onRefresh={async () => {
            await mobileFetch(true)
          }}
          emptyMessage={<p class="py-10 text-center text-foreground-subtle">Aún no has creado cotizaciones</p>}
          endMessage={<span>Has llegado al final de las cotizaciones</span>}
          class="space-y-3"
          renderItem={quote => (
            <SwipeAction
              itemId={quote.id}
              rememberPosition
              onClick={() => navigate(`/admin/cotizaciones/${quote.id}` as Path)}
              leftOptions={[
                ...(can(P.quotes.update)
                  ? [
                      {
                        icon: 'material-symbols-light:edit-outline',
                        label: 'Editar',
                        color: 'warning',
                        action: () => navigate(`/admin/cotizaciones/${quote.id}` as Path)
                      }
                    ]
                  : [])
              ]}
              rightOptions={[
                ...(can(P.quotes.delete)
                  ? [
                      {
                        icon: 'material-symbols-light:delete-outline',
                        label: 'Eliminar',
                        color: 'danger',
                        action: () => handleDelete(quote)
                      }
                    ]
                  : [])
              ]}
            >
              <Card class="!rounded-lg">
                <Card.Body>
                  <div class="flex items-start gap-3">
                    <Show when={can(P.quotes.delete)}>
                      <Checkbox size="sm" color="danger" checked={selectedIds().has(quote.id)} onChange={() => toggleSelect(quote.id)} class="mt-0.5 shrink-0" />
                    </Show>
                    <div class="min-w-0 flex-1">
                      <div class="flex items-center justify-between gap-2">
                        <span class="font-medium text-foreground">{quote.number}</span>
                        <Chip color={statusColors[quote.status]} variant="soft" size="xs">
                          {statusLabels[quote.status]}
                        </Chip>
                      </div>
                      <p class="text-sm text-foreground truncate mt-0.5">{quote.title}</p>
                      <p class="text-xs text-foreground-subtle truncate">{quote.customer?.name ?? '-'}</p>
                      <div class="flex items-center justify-between gap-2 mt-2">
                        <span class="text-sm font-semibold text-foreground">{formatMoney(quote.total, quote.currency, settings()?.currencies ?? [])}</span>
                        <span class="text-xs text-foreground-subtle">{quote.items?.length ?? 0} servicio(s)</span>
                      </div>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </SwipeAction>
          )}
        />
      </Show>

      <Modal isOpen={!!sending()} onClose={() => setSending(null)} size="md">
        <Modal.Header>
          <h2 class="text-xl font-bold text-foreground">Enviar propuesta</h2>
          <p class="text-sm text-foreground-subtle">{sending()?.number} · el PDF se adjunta automáticamente</p>
        </Modal.Header>
        <Modal.Body>
          <Show when={sending()} keyed>
            {_quote => (
              <Form id="quote-send-form" controller={sendForm}>
                <div class="space-y-4">
                  <Input name="to" label="Destinatario" type="email" placeholder="cliente@empresa.com" required />
                  <Input name="subject" label="Asunto" />
                  <Textarea name="message" label="Mensaje" rows={5} placeholder="Déjalo vacío para usar el mensaje por defecto con el resumen de la propuesta" helperText="Admite markdown" />
                </div>
              </Form>
            )}
          </Show>
        </Modal.Body>
        <Modal.Footer>
          <div class="flex justify-end gap-2">
            <Button type="button" variant="soft" color="default" onClick={() => setSending(null)}>
              Cancelar
            </Button>
            <Button type="submit" form="quote-send-form" disabled={sendForm.submitting()} prependIcon="material-symbols-light:send-outline">
              {sendForm.submitting() ? 'Enviando...' : 'Enviar'}
            </Button>
          </div>
        </Modal.Footer>
      </Modal>

      {/* FAB móvil: mismas acciones del header, accesibles en pantallas pequeñas */}
      <div class="sm:hidden">
        <FabMenu
          items={[...(can(P.quotes.create) ? [{ label: 'Nueva Cotización', icon: 'material-symbols-light:add-circle-outline', onClick: () => navigate('/admin/cotizaciones/nueva' as Path) }] : [])]}
        />
      </div>

      <alert.AlertComponent />
    </>
  )
}

export default QuotesPage
